import React, { useEffect, useState } from "react";
import { apiFetch } from "../api/client";

const formatCurrency = (value) => `₦${(Number(value) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function SectionCard({ title, children }) {
    return (
        <section className="rounded-xl border border-[#8A17D1]/30 bg-[#111B31] p-5 shadow-lg shadow-[#020617]/30">
            <h2 className="text-lg font-semibold text-[#E6EEF3]">{title}</h2>
            <div className="mt-4">{children}</div>
        </section>
    );
}

function Spinner({ size = "h-5 w-5" }) {
    return (
        <span
            className={`inline-block ${size} animate-spin rounded-full border-2 border-[#D326AD]/30 border-t-[#D326AD]`}
            aria-hidden="true"
        />
    );
}

function ProductList({ items = [], empty = "No data yet." }) {
    if (!items.length) return <p className="text-sm text-[#C7D2FE]">{empty}</p>;
    return (
        <div className="space-y-3">
            {items.slice(0, 6).map((item) => (
                <div key={item.productId || item.productName} className="rounded-lg border border-[#C7D2FE]/10 bg-[#071026]/60 p-3">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <p className="font-medium text-[#E6EEF3]">{item.productName}</p>
                        <p className="text-sm text-[#f3a1dc]">{item.quantitySold || 0} sold · {item.currentStock || 0} in stock</p>
                    </div>
                    <p className="mt-1 text-xs text-[#C7D2FE]">Revenue {formatCurrency(item.revenue)} · Profit {formatCurrency(item.estimatedProfit)} · Margin {item.marginPercent || 0}%</p>
                </div>
            ))}
        </div>
    );
}

export default function AiPerformance() {
    const [health, setHealth] = useState(null);
    const [actions, setActions] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [performance, setPerformance] = useState(null);
    const [report, setReport] = useState(null);
    const [reportQuery, setReportQuery] = useState("Create a sales report");
    const [loading, setLoading] = useState(true);
    const [reportLoading, setReportLoading] = useState(false);
    const [error, setError] = useState("");

    const loadAll = async () => {
        setLoading(true);
        setError("");
        try {
            const [healthRes, actionsRes, forecastRes, performanceRes] = await Promise.all([
                apiFetch("/api/ai/business-health"),
                apiFetch("/api/ai/action-checklist"),
                apiFetch("/api/ai/sales-forecast"),
                apiFetch("/api/ai/product-performance"),
            ]);

            if ([healthRes, actionsRes, forecastRes, performanceRes].some((res) => res.status === 403)) {
                setError("Upgrade to Premium to view AI performance insights.");
                return;
            }

            const healthBody = await healthRes.json().catch(() => null);
            const actionsBody = await actionsRes.json().catch(() => null);
            const forecastBody = await forecastRes.json().catch(() => null);
            const performanceBody = await performanceRes.json().catch(() => null);

            setHealth(healthBody?.data || healthBody);
            setActions(actionsBody?.data || actionsBody);
            setForecast(forecastBody?.data || forecastBody);
            setPerformance(performanceBody?.data || performanceBody);
        } catch (err) {
            console.error("Unable to load AI performance", err);
            setError("Unable to load AI performance insights right now.");
        } finally {
            setLoading(false);
        }
    };

    const createReport = async (event) => {
        event.preventDefault();
        setReportLoading(true);
        setError("");
        try {
            const res = await apiFetch(`/api/ai/report?query=${encodeURIComponent(reportQuery)}`);
            const body = await res.json().catch(() => null);
            if (!res.ok || body?.succeeded === false) {
                setError(body?.message || "Unable to create AI report.");
                return;
            }
            setReport(body?.data || body);
        } catch (err) {
            console.error("Unable to create AI report", err);
            setError("Unable to create AI report right now.");
        } finally {
            setReportLoading(false);
        }
    };

    const downloadReport = () => {
        if (!report?.rows?.length) return;
        const header = ["Product", "Quantity Sold", "Revenue", "Estimated Profit", "Margin %"];
        const rows = report.rows.map((row) => [row.productName, row.quantitySold, row.revenue, row.estimatedProfit, row.marginPercent]);
        const csv = [header, ...rows].map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "stokraze-ai-report.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        loadAll();
    }, []);

    if (loading) {
        return (
            <main className="col-span-12 lg:col-span-10 flex min-h-[50vh] items-center justify-center p-6">
                <div className="rounded-2xl border border-[#8A17D1]/30 bg-[#111B31] px-8 py-7 text-center shadow-lg shadow-[#020617]/30">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#8A17D1]/15">
                        <Spinner size="h-8 w-8" />
                    </div>
                    <h1 className="mt-4 text-lg font-semibold text-[#E6EEF3]">Generating AI performance review...</h1>
                    <p className="mt-2 text-sm text-[#C7D2FE]">Please wait while Stokraze reviews your sales, stock, profit, and product performance.</p>
                </div>
            </main>
        );
    }

    if (error && !health) {
        return <main className="col-span-12 lg:col-span-10 p-6"><div className="rounded-xl border border-[#D326AD]/30 bg-[#111B31] p-6 text-[#E6EEF3]">{error}</div></main>;
    }

    return (
        <main className="col-span-12 lg:col-span-10 grid grid-cols-1 gap-5 p-4 lg:grid-cols-2">
            <header className="col-span-full rounded-xl border border-[#8A17D1]/30 bg-[#111B31] p-6">
                <p className="text-sm font-semibold uppercase tracking-wide text-[#f3a1dc]">Premium AI</p>
                <h1 className="mt-1 text-2xl font-bold text-[#E6EEF3]">AI Business Performance</h1>
                <p className="mt-2 text-sm text-[#C7D2FE]">Health score, action checklist, forecast, product performance, and natural-language reports.</p>
            </header>

            <SectionCard title="Business Health Score">
                <div className="flex items-center gap-4">
                    <div className="flex h-24 w-24 items-center justify-center rounded-full border-4 border-[#D326AD] text-2xl font-bold text-[#E6EEF3]">{health?.score ?? 0}</div>
                    <div>
                        <p className="text-lg font-semibold text-[#f3a1dc]">{health?.status}</p>
                        <p className="text-sm text-[#C7D2FE]">This week: {formatCurrency(health?.revenueThisWeek)} · Change: {health?.revenueChangePercent || 0}%</p>
                    </div>
                </div>
                <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-[#C7D2FE]">
                    {(health?.reasons || []).map((reason, index) => <li key={index}>{reason}</li>)}
                </ul>
            </SectionCard>

            <SectionCard title="AI Action Checklist">
                <ul className="space-y-2 text-sm text-[#C7D2FE]">
                    {(actions?.actions || []).map((action, index) => <li key={index} className="rounded-lg bg-[#071026]/60 p-3">{action}</li>)}
                </ul>
            </SectionCard>

            <SectionCard title="Sales Forecast">
                <p className="text-2xl font-bold text-[#E6EEF3]">{formatCurrency(forecast?.forecastLow)} - {formatCurrency(forecast?.forecastHigh)}</p>
                <p className="mt-2 text-sm text-[#C7D2FE]">{forecast?.summary}</p>
            </SectionCard>

            <SectionCard title="Product Performance">
                <div className="grid gap-4">
                    <div><h3 className="mb-2 font-semibold text-[#f3a1dc]">Best sellers</h3><ProductList items={performance?.bestSellers} /></div>
                    <div><h3 className="mb-2 font-semibold text-[#f3a1dc]">Slow movers</h3><ProductList items={performance?.slowMovers} /></div>
                    <div><h3 className="mb-2 font-semibold text-[#f3a1dc]">Low margin products</h3><ProductList items={performance?.lowMarginProducts} /></div>
                    <div><h3 className="mb-2 font-semibold text-[#f3a1dc]">Low-stock risks</h3><ProductList items={performance?.lowStockRisks} /></div>
                </div>
            </SectionCard>

            <SectionCard title="Natural-Language Reports">
                <form onSubmit={createReport} className="flex flex-col gap-3 sm:flex-row">
                    <input value={reportQuery} onChange={(event) => setReportQuery(event.target.value)} className="flex-1 rounded-lg border border-[#C7D2FE]/15 bg-[#071026]/70 p-3 text-sm text-[#E6EEF3]" placeholder="Create a sales report for this month" />
                    <button
                        disabled={reportLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#8A17D1] px-4 py-2 text-sm font-semibold text-white hover:bg-[#D326AD] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {reportLoading && <Spinner />}
                        {reportLoading ? "Generating..." : "Generate"}
                    </button>
                </form>
                {report && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="font-semibold text-[#E6EEF3]">{report.title}</p>
                                <p className="text-sm text-[#C7D2FE]">{report.summary}</p>
                            </div>
                            <button onClick={downloadReport} className="rounded-lg border border-[#D326AD]/50 px-3 py-2 text-sm text-[#f3a1dc]">Download CSV</button>
                        </div>
                        <div className="mt-3 overflow-x-auto">
                            <table className="w-full text-left text-sm text-[#C7D2FE]">
                                <thead><tr className="border-b border-[#C7D2FE]/15"><th className="py-2">Product</th><th>Sold</th><th>Revenue</th><th>Profit</th><th>Margin</th></tr></thead>
                                <tbody>{(report.rows || []).map((row, index) => <tr key={index} className="border-b border-[#C7D2FE]/10"><td className="py-2">{row.productName}</td><td>{row.quantitySold}</td><td>{formatCurrency(row.revenue)}</td><td>{formatCurrency(row.estimatedProfit)}</td><td>{row.marginPercent}%</td></tr>)}</tbody>
                            </table>
                        </div>
                    </div>
                )}
            </SectionCard>
        </main>
    );
}
